import { parsePhoneNumberWithError } from "libphonenumber-js";
import dayjs from "dayjs";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as XLSX from "xlsx";

// pdfMake is a shared singleton. Set its font bundle in the module that owns
// the PDF helpers so these functions remain self-sufficient regardless of
// which page was loaded first.
//
// pdfmake's vfs_fonts.js export shape has changed across patch releases
// within the same ^0.2.x range: older builds nest the font map under
// `pdfFonts.pdfMake.vfs`, newer ones (0.2.23+) export the font map itself
// with no wrapper at all. Falling through both shapes keeps this working
// regardless of which patch version npm resolves.
pdfMake.vfs = pdfFonts?.pdfMake?.vfs || pdfFonts?.vfs || pdfFonts;



const scrollToExtreme = (direction) => {
    if (direction === "up") {
        window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (direction === "down") {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } else if (direction === "left") {
        window.scrollTo({ left: 0, behavior: "smooth" });
    } else if (direction === "right") {
        window.scrollTo({ left: document.body.scrollWidth, behavior: "smooth" });
    } else {
        console.error("Invalid direction. Use 'up', 'down', 'left', or 'right'.");
    }
};

function getCountryCode(phoneNumber) {
    phoneNumber = phoneNumber.replaceAll("+", "");
    phoneNumber = phoneNumber[0] == '0' ? phoneNumber.replace("0", "971") : phoneNumber;

    // Add a "+" sign to the phone number to make it a valid international format
    const internationalPhoneNumber = `+${phoneNumber}`;

    // Parse the phone number
    const parsedNumber = parsePhoneNumberWithError(internationalPhoneNumber);

    // Check if the number is valid
    if (!parsedNumber) {
        // alert(internationalPhoneNumber)
        throw new Error('Invalid phone number');
    }

    // Return the country code (e.g., "IN" for India)
    // return parsedNumber.country == 'AE' ? 'ARE' : parsedNumber.country;
    return parsedNumber.country;
};

const openNewTab = (url) => {
    if (!url) {
        console.error("You forgot the URL, dummy!");
        return;
    }
    window.open(url, '_blank').focus();
}

const playChime = (file) => {
    if (file) {
        const audio = new Audio(file); // Adjust path if needed
        audio.play().catch((error) => console.error("Failed to play sound:", error));
        return true;
    }
    else {
        return false;
    }
};

const showNotification = (main, body, logo, options = {}) => {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const systemNotification = new Notification(main ? main : "Hello!", {
            body: body ? body : "This is a system-level notification.",
            icon: logo
                ? logo
                : "https://www.uzdpart.com/wp-content/themes/uzdpart-child/assets/images/logo.PNG",
            tag: options.tag,
        });
        if (typeof options.onClick === "function") {
            systemNotification.onclick = () => {
                window.focus();
                options.onClick();
                systemNotification.close();
            };
        }
        return systemNotification;
    } else {
        console.log("Notification permission not granted.");
        return null;
    }
};

const generatePDF = (data, currentTabState, startDate, endDate, supplierIdToName) => {
    const docDefinition = {
        pageSize: "A3",
        pageOrientation: "landscape",
        content: [
            {
                text: `Results between (${dayjs(startDate).format(
                    "YYYY-MM-DD"
                )} ~ ${dayjs(endDate).format("YYYY-MM-DD")}), Status : ${currentTabState ? currentTabState : "<All Statuses>"
                    }, Total Rows : ${data.length}`,
                margin: [0, 20, 0, 8],
            },
            {
                style: "tableExample",
                table: {
                    body: [
                        [
                            { text: "SlNo", style: "tableHeader" },
                            { text: "Query ID", style: "tableHeader" },
                            { text: "User", style: "tableHeader" },
                            { text: "Date", style: "tableHeader" },
                            { text: "Make", style: "tableHeader" },
                            { text: "Model", style: "tableHeader" },
                            { text: "Year", style: "tableHeader" },
                            { text: "Part", style: "tableHeader" },
                            { text: "Customer Name", style: "tableHeader" },
                            { text: "Phone", style: "tableHeader" },
                            { text: "Supplier", style: "tableHeader" },
                            { text: "Best Price Received", style: "tableHeader" },
                            { text: "Price Given", style: "tableHeader" },
                            { text: "Status-Remarks", style: "tableHeader" },
                        ],
                        ...data.map((item, key) => [
                            key + 1,
                            item.query_id,
                            item.user,
                            item.date,
                            item.make,
                            item.model,
                            item.year,
                            item.body_part,
                            item.cus_name || "N/A",
                            item.phone || "N/A",
                            item.supplier != null &&
                                item.supplier != "" &&
                                item.supplier != undefined
                                ? supplierIdToName[item.supplier] ?? "N/A"
                                : "N/A",
                            item.best_price_received,
                            item.price_given,
                            item.status,
                        ]),
                    ],
                    styles: {
                        header: {
                            fontSize: 18,
                            bold: true,
                            margin: [0, 0, 0, 10],
                        },
                        subheader: {
                            fontSize: 16,
                            bold: true,
                            margin: [0, 10, 0, 5],
                        },
                        tableExample: {
                            margin: [0, 5, 0, 15],
                        },
                        tableHeader: {
                            bold: true,
                            fontSize: 13,
                            color: "black",
                        },
                    },
                },
                layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                        return rowIndex % 2 === 0 && rowIndex != 0
                            ? "#CCCCCC"
                            : rowIndex == 0
                                ? "#2AFD00"
                                : null;
                    },
                },
            },
        ],
    };

    pdfMake.createPdf(docDefinition).download(`queries_${dayjs(startDate).format("YYYY-MM-DD")}_${dayjs(endDate).format("YYYY-MM-DD")}${currentTabState ? "_" + currentTabState.replaceAll(" ", "_") : ""}.pdf`);
    //not needed as almost all modern browsers open PDFs directly automatically after downloading
    // pdfMake.createPdf(docDefinition).open();

};

const generateExcel = (data, currentTabState, startDate, endDate, supplierIdToName) => {

    // Convert data to worksheet format
    const worksheetData = [
        [
            "SlNo",
            "Query ID",
            "User",
            "Date",
            "Make",
            "Model",
            "Year",
            "Part",
            "Customer Name",
            "Phone",
            "Supplier",
            "Best Price Received",
            "Price Given",
            "Status-Remarks",
        ],
        ...data.map((item, key) => [
            key + 1,
            item.query_id,
            item.user,
            item?.timeline[0]
                ? (() => {
                    const [key, val] = Object.entries(item.timeline[0])[0];
                    return val;
                })()
                : item.date,
            item.make,
            item.model,
            item.year,
            item.body_part,
            item.cus_name || "N/A",
            item.phone || "N/A",
            item.supplier != ""
                ? supplierIdToName[item.supplier] ?? "N/A"
                : "N/A",
            item.best_price_received,
            item.price_given,
            item.status,
        ]),
    ];

    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Append worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Queries");

    // Format the filename
    const filename = `queries_${dayjs(startDate).format(
        "YYYY-MM-DD"
    )}_${dayjs(endDate).format("YYYY-MM-DD")}${currentTabState ? "_" + currentTabState.replaceAll(" ", "_") : ""
        }.xlsx`;

    // Write workbook and download
    XLSX.writeFile(workbook, filename);

};

// Builds a PDF of QuerysetV4's (querysetV2 schema) currently-loaded table
// rows and resolves with its base64 content (no "data:" prefix) instead of
// triggering a download — generatePDF above is download-only and built for
// the legacy queryset table's flat row shape, this is for sending the PDF
// on as a WATI document instead.
// `columns` is the user's enabled subset from qv4/exportColumns.js
// (getEnabledExportColumns) — SlNo is always included regardless, it's row
// numbering rather than a real data field.
const generateQv4QueriesPdfBase64 = (rows, statusTabLabel, columns) => {
    const docDefinition = {
        pageSize: "A3",
        pageOrientation: "landscape",
        content: [
            {
                text: `${statusTabLabel || "All"} — ${dayjs().format("YYYY-MM-DD hh:mm A")}, Total Rows : ${rows.length}`,
                margin: [0, 20, 0, 8],
            },
            {
                style: "tableExample",
                table: {
                    headerRows: 1,
                    body: [
                        [
                            { text: "SlNo", style: "tableHeader" },
                            ...columns.map(c => ({ text: c.label, style: "tableHeader" })),
                        ],
                        ...rows.map((r, key) => [
                            key + 1,
                            ...columns.map(c => (c.getPdfValue ? c.getPdfValue(r) : c.getValue(r))),
                        ]),
                    ],
                    styles: {
                        tableExample: { margin: [0, 5, 0, 15] },
                        tableHeader: { bold: true, fontSize: 13, color: "black" },
                    },
                },
                layout: {
                    fillColor: function (rowIndex) {
                        return rowIndex % 2 === 0 && rowIndex !== 0 ? "#CCCCCC" : rowIndex === 0 ? "#2AFD00" : null;
                    },
                },
            },
        ],
    };

    return new Promise((resolve) => {
        pdfMake.createPdf(docDefinition).getBase64((base64) => resolve(base64));
    });
};

// Same row shape/columns as generateQv4QueriesPdfBase64 above (both draw
// from qv4/exportColumns.js), kept in sync deliberately — this is the Excel
// counterpart offered alongside the PDF download in QuerysetV4's
// Send/Export Queries modal, not the legacy generateExcel (different
// schema, old queryset table).
const generateQv4QueriesExcel = (rows, statusTabLabel, columns) => {
    const worksheetData = [
        ["SlNo", ...columns.map(c => c.label)],
        ...rows.map((r, key) => [key + 1, ...columns.map(c => c.getValue(r))]),
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Queries");

    const filename = `queries_${(statusTabLabel || "All").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "")}_${dayjs().format("YYYY-MM-DD_HH-mm")}.xlsx`;
    XLSX.writeFile(workbook, filename);
};

export {
    scrollToExtreme,
    getCountryCode,
    openNewTab,
    playChime,
    showNotification,
    generatePDF,
    generateExcel,
    generateQv4QueriesPdfBase64,
    generateQv4QueriesExcel,
};
